import React, { useState } from 'react';
import { apiService } from '../../services/api';
import ModalPortal from '../Common/ModalPortal';
import './RegisterModal.css';

interface RegisterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRegisterSuccess: () => void;
}

interface FormData {
  // Dados da empresa
  cnpj: string;
  razao_social: string;
  nome_fantasia: string;
  email_empresa: string;
  telefone_empresa: string;
  endereco: string;
  
  // Dados do usuário administrador
  nome_usuario: string;
  email_usuario: string;
  senha: string;
  confirmar_senha: string;
  telefone_usuario: string;
}

const RegisterModal: React.FC<RegisterModalProps> = ({ isOpen, onClose, onRegisterSuccess }) => {
  const [formData, setFormData] = useState<FormData>({
    cnpj: '',
    razao_social: '',
    nome_fantasia: '',
    email_empresa: '',
    telefone_empresa: '',
    endereco: '',
    nome_usuario: '',
    email_usuario: '',
    senha: '',
    confirmar_senha: '',
    telefone_usuario: ''
  });

  const [currentStep, setCurrentStep] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState<Partial<FormData>>({});
  const [apiError, setApiError] = useState<string>('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Limpar erro do campo quando o usuário começar a digitar
    if (errors[name as keyof FormData]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
    
    // Limpar erro da API quando o usuário começar a digitar
    if (apiError) {
      setApiError('');
    }
  };

  const validateStep1 = (): boolean => {
    const newErrors: Partial<FormData> = {};
    
    if (!formData.cnpj.trim()) {
      newErrors.cnpj = 'CNPJ é obrigatório';
    } else if (formData.cnpj.replace(/\D/g, '').length < 14) {
      newErrors.cnpj = 'CNPJ deve ter 14 dígitos';
    }
    
    if (!formData.razao_social.trim()) {
      newErrors.razao_social = 'Razão social é obrigatória';
    }
    
    if (!formData.email_empresa.trim()) {
      newErrors.email_empresa = 'Email da empresa é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.email_empresa)) {
      newErrors.email_empresa = 'Email inválido';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStep2 = (): boolean => {
    const newErrors: Partial<FormData> = {};
    
    if (!formData.nome_usuario.trim()) {
      newErrors.nome_usuario = 'Nome do usuário é obrigatório';
    }
    
    if (!formData.email_usuario.trim()) {
      newErrors.email_usuario = 'Email do usuário é obrigatório';
    } else if (!/\S+@\S+\.\S+/.test(formData.email_usuario)) {
      newErrors.email_usuario = 'Email inválido';
    }
    
    if (!formData.senha) {
      newErrors.senha = 'Senha é obrigatória';
    } else if (formData.senha.length < 8) {
      newErrors.senha = 'Senha deve ter pelo menos 8 caracteres';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.senha)) {
      newErrors.senha = 'Senha deve conter letras maiúsculas, minúsculas e números';
    }
    
    if (!formData.confirmar_senha) {
      newErrors.confirmar_senha = 'Confirmação de senha é obrigatória';
    } else if (formData.senha !== formData.confirmar_senha) {
      newErrors.confirmar_senha = 'As senhas não coincidem';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep1()) {
      setCurrentStep(2);
    }
  };

  const handleBack = () => {
    setCurrentStep(1);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateStep2()) {
      return;
    }

    setIsLoading(true);
    setApiError('');
    
    try {
      // Remover campo de confirmação de senha antes de enviar para a API
      const { confirmar_senha, ...dadosParaEnviar } = formData;
      
      // Usar o serviço de API para registrar a empresa
      const response = await apiService.post('REGISTER', dadosParaEnviar);
      
      console.log('Registro realizado com sucesso:', response);
      onRegisterSuccess();
      onClose();
      alert('Empresa e usuário administrador criados com sucesso! Verifique seu email para confirmar a conta.');
    } catch (error: any) {
      console.error('Erro no registro:', error);
      
      // Mostrar detalhes do erro de validação se disponível
      let errorMessage = 'Erro ao realizar registro. Tente novamente.';
      
      if (error.message && error.message.includes('400')) {
        errorMessage = 'Dados inválidos. Verifique se todos os campos estão preenchidos corretamente.';
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      // Tentar extrair detalhes de validação da resposta
      if (error.response && error.response.data && error.response.data.details) {
        const validationErrors = error.response.data.details;
        const fieldErrors = validationErrors.map((err: any) => 
          `${err.field}: ${err.message}`
        ).join(', ');
        errorMessage = `Erro de validação: ${fieldErrors}`;
      }
      
      setApiError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      cnpj: '',
      razao_social: '',
      nome_fantasia: '',
      email_empresa: '',
      telefone_empresa: '',
      endereco: '',
      nome_usuario: '',
      email_usuario: '',
      senha: '',
      confirmar_senha: '',
      telefone_usuario: ''
    });
    setCurrentStep(1);
    setErrors({});
    onClose();
  };

  return (
    <ModalPortal isOpen={isOpen}>
      <div className="modal-overlay" onClick={handleClose}>
        <div className="modal-content" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Registrar Nova Empresa</h2>
          <button className="close-button" onClick={handleClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="progress-bar">
            <div className={`progress-step ${currentStep >= 1 ? 'active' : ''}`}>
              <span>1</span>
              <label>Dados da Empresa</label>
            </div>
            <div className="progress-line"></div>
            <div className={`progress-step ${currentStep >= 2 ? 'active' : ''}`}>
              <span>2</span>
              <label>Usuário Administrador</label>
            </div>
          </div>

          {apiError && (
            <div className="error-message" style={{ display: 'block', marginBottom: '20px' }}>
              {apiError}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {currentStep === 1 && (
              <div className="form-step">
                <h3>Dados da Empresa</h3>
                
                <div className="form-group">
                  <label htmlFor="cnpj">CNPJ *</label>
                  <input
                    type="text"
                    id="cnpj"
                    name="cnpj"
                    value={formData.cnpj}
                    onChange={handleInputChange}
                    placeholder="00.000.000/0000-00"
                    className={errors.cnpj ? 'error' : ''}
                  />
                  {errors.cnpj && <span className="error-message">{errors.cnpj}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="razao_social">Razão Social *</label>
                  <input
                    type="text"
                    id="razao_social"
                    name="razao_social"
                    value={formData.razao_social}
                    onChange={handleInputChange}
                    placeholder="Nome legal da empresa"
                    className={errors.razao_social ? 'error' : ''}
                  />
                  {errors.razao_social && <span className="error-message">{errors.razao_social}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="nome_fantasia">Nome Fantasia</label>
                  <input
                    type="text"
                    id="nome_fantasia"
                    name="nome_fantasia"
                    value={formData.nome_fantasia}
                    onChange={handleInputChange}
                    placeholder="Nome comercial da empresa (opcional)"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="email_empresa">Email da Empresa *</label>
                  <input
                    type="email"
                    id="email_empresa"
                    name="email_empresa"
                    value={formData.email_empresa}
                    onChange={handleInputChange}
                    placeholder="empresa@exemplo.com"
                    className={errors.email_empresa ? 'error' : ''}
                  />
                  {errors.email_empresa && <span className="error-message">{errors.email_empresa}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="telefone_empresa">Telefone da Empresa</label>
                  <input
                    type="tel"
                    id="telefone_empresa"
                    name="telefone_empresa"
                    value={formData.telefone_empresa}
                    onChange={handleInputChange}
                    placeholder="(11) 99999-9999"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="endereco">Endereço</label>
                  <textarea
                    id="endereco"
                    name="endereco"
                    value={formData.endereco}
                    onChange={handleInputChange}
                    placeholder="Endereço completo da empresa"
                    rows={3}
                  />
                </div>

                <div className="form-actions">
                  <button type="button" onClick={handleNext} className="next-button">
                    Próximo
                  </button>
                </div>
              </div>
            )}

            {currentStep === 2 && (
              <div className="form-step">
                <h3>Usuário Administrador</h3>
                
                <div className="form-group">
                  <label htmlFor="nome_usuario">Nome Completo *</label>
                  <input
                    type="text"
                    id="nome_usuario"
                    name="nome_usuario"
                    value={formData.nome_usuario}
                    onChange={handleInputChange}
                    placeholder="Seu nome completo"
                    className={errors.nome_usuario ? 'error' : ''}
                  />
                  {errors.nome_usuario && <span className="error-message">{errors.nome_usuario}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="email_usuario">Email *</label>
                  <input
                    type="email"
                    id="email_usuario"
                    name="email_usuario"
                    value={formData.email_usuario}
                    onChange={handleInputChange}
                    placeholder="seu.email@exemplo.com"
                    className={errors.email_usuario ? 'error' : ''}
                  />
                  {errors.email_usuario && <span className="error-message">{errors.email_usuario}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="telefone_usuario">Telefone</label>
                  <input
                    type="tel"
                    id="telefone_usuario"
                    name="telefone_usuario"
                    value={formData.telefone_usuario}
                    onChange={handleInputChange}
                    placeholder="(11) 99999-9999"
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="senha">Senha *</label>
                  <input
                    type="password"
                    id="senha"
                    name="senha"
                    value={formData.senha}
                    onChange={handleInputChange}
                    placeholder="Mínimo 8 caracteres com letras e números"
                    className={errors.senha ? 'error' : ''}
                  />
                  {errors.senha && <span className="error-message">{errors.senha}</span>}
                </div>

                <div className="form-group">
                  <label htmlFor="confirmar_senha">Confirmar Senha *</label>
                  <input
                    type="password"
                    id="confirmar_senha"
                    name="confirmar_senha"
                    value={formData.confirmar_senha}
                    onChange={handleInputChange}
                    placeholder="Digite a senha novamente"
                    className={errors.confirmar_senha ? 'error' : ''}
                  />
                  {errors.confirmar_senha && <span className="error-message">{errors.confirmar_senha}</span>}
                </div>

                <div className="form-actions">
                  <button type="button" onClick={handleBack} className="back-button">
                    Voltar
                  </button>
                  <button type="submit" disabled={isLoading} className="submit-button">
                    {isLoading ? 'Registrando...' : 'Finalizar Registro'}
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
        </div>
      </div>
    </ModalPortal>
  );
};

export default RegisterModal;
